/**
 * Create-Wizard — die Anfrage an den Server. Getrennt vom Formularmodell, weil hier entschieden
 * wird, was der Server selbst bestimmt: Zeitzone und Währung eines bestehenden Objekts und die des
 * Workspace für ein neues.
 */
import type { CreatePropertyRequest, WorkspaceResponse } from '@/shared/api';
import { isCompleteAddress, type WizardForm } from './wizardModel';

/** Welche Werte des neuen Objekts jemand im Wizard selbst geändert hat. */
export interface ObjectValuesTouched {
  timezone: boolean;
  currency: boolean;
}

export const UNTOUCHED: ObjectValuesTouched = { timezone: false, currency: false };

/**
 * Zeigt Zeitzone und Währung des Workspace an, solange niemand sie geändert hat. Eine eigene
 * Eingabe gewinnt, auch wenn der Workspace erst danach ankommt.
 */
export function withWorkspaceDefaults(
  form: WizardForm,
  workspace: Pick<WorkspaceResponse, 'timeZone' | 'defaultCurrency'>,
  touched: ObjectValuesTouched = UNTOUCHED,
): WizardForm {
  return {
    ...form,
    timezone: touched.timezone ? form.timezone : (workspace.timeZone ?? form.timezone),
    currency: touched.currency ? form.currency : (workspace.defaultCurrency ?? form.currency),
  };
}

/**
 * Ein bestehendes Objekt behält seine Werte. Ein neues bekommt nur, was hier jemand geändert hat;
 * alles andere setzt der Server aus dem Workspace. Die festen Werte des Formulars wissen nichts über
 * den Workspace — kam er nicht an, stünde sonst still Europe/Berlin und EUR im neuen Objekt.
 */
function objectValues(form: WizardForm, touched: ObjectValuesTouched) {
  if (form.locationMode === 'existing') return {};
  return {
    ...(touched.timezone ? { timezone: form.timezone } : {}),
    ...(touched.currency ? { currency: form.currency.toUpperCase() } : {}),
  };
}

export function buildCreateRequest(
  form: WizardForm,
  touched: ObjectValuesTouched = UNTOUCHED,
): CreatePropertyRequest {
  const objectRef =
    form.locationMode === 'existing'
      ? { existingObjectId: form.existingObjectId }
      : {
          newObject: {
            address: {
              street: `${form.streetName.trim()} ${form.houseNumber.trim()}`.trim(),
              streetName: form.streetName.trim(),
              houseNumber: form.houseNumber.trim(),
              city: form.city,
              postalCode: form.postalCode,
              countryCode: form.countryCode.toUpperCase(),
              latitude: form.latitude,
              longitude: form.longitude,
            },
            name: form.objectName.trim() || undefined,
            needsAddressReview: !isCompleteAddress(form),
          },
        };
  return {
    name: form.name,
    slug: form.slug,
    ...(form.description.trim() ? { description: form.description.trim() } : {}),
    propertyType: form.propertyType,
    ...objectValues(form, touched),
    objectRef,
    occupancyAdults: form.occupancyAdults,
    occupancyChildren: form.occupancyChildren,
    bedrooms: form.bedrooms,
    bathrooms: form.bathrooms,
    cleaningFee: form.cleaningFee,
    baseNightlyRate: form.baseNightlyRate,
    // Draft-first: der Wizard löst keine Provisionierung aus; Publish läuft explizit (G2).
    autoProvisionToChannex: false,
  };
}
